import { format } from 'date-fns'
import * as Effects from 'redux-saga/effects'
import { callFetch } from '../../actions/fetch'
import { PriceGridAction } from '../../reducers/price/actionNames'
import { getUserObjectId } from '../../reducers/userObject/selectors/object'
import { ActionNames } from '../../utils/FetchActionsName'
import fetchActionsName from '../../utils/FetchActionsName/fetchActionsName'
import copyPricelistModel from '../../models/Price/copyPricelist'
import {
  computeImmediatelyBefore,
  computePreviousYear,
} from '../../screens/PriceGrid2/Components/copyPricesModal/sourcePeriodUtils'
import { CopyPricesWizardData } from '../../screens/PriceGrid2/Components/copyPricesModal/wizardTypes'
import { clearAllPrecomputedPriceCaches } from '../../reducers/bookingCalendar/selectors/rooms'

function getSourceDates(wizardData: CopyPricesWizardData): { from: Date; to: Date } {
  if (wizardData.sourceDateFrom && wizardData.sourceDateTo) {
    return {
      from: wizardData.sourceDateFrom,
      to: wizardData.sourceDateTo,
    }
  }

  const targetFrom = wizardData.targetDateFrom!
  const targetTo = wizardData.targetDateTo!
  const option = wizardData.sourcePeriodOption ?? 'previous_year'

  if (option === 'immediately_before') {
    const { sourceFrom, sourceTo } = computeImmediatelyBefore(targetFrom, targetTo)
    return { from: sourceFrom, to: sourceTo }
  }

  const { sourceFrom, sourceTo } = computePreviousYear(targetFrom, targetTo)
  return { from: sourceFrom, to: sourceTo }
}

function* copyPricelistSaga(
  action: { type: string; wizardData: CopyPricesWizardData },
) {
  try {
    const actionNames = fetchActionsName(ActionNames.CopyPricelist)
    const [{ calendarId, service }, objectId] = yield Effects.all([
      Effects.take(actionNames.output),
      Effects.select(getUserObjectId),
      Effects.put(
        callFetch(actionNames.input, actionNames.output, copyPricelistModel),
      ),
    ])

    if (!objectId || !calendarId) {
      yield Effects.delay(200)
      yield* copyPricelistSaga(action)
      return
    }

    const { wizardData } = action
    const source = getSourceDates(wizardData)
    const roomIds = wizardData.roomIds
    const roomsArray = Array.isArray(roomIds)
      ? roomIds.map(id => Number(id))
      : Object.values(roomIds || {}).map(id => Number(id))

    const payload = {
      id_calendar: Number(calendarId),
      id_object: Number(objectId),
      rooms: roomsArray,
      targetDateFrom: format(wizardData.targetDateFrom!, 'YYYY-MM-DD'),
      targetDateTo: format(wizardData.targetDateTo!, 'YYYY-MM-DD'),
      sourceDateFrom: format(source.from, 'YYYY-MM-DD'),
      sourceDateTo: format(source.to, 'YYYY-MM-DD'),
      increaseValue: wizardData.increaseValue ?? 0,
      increaseUnit: (wizardData.increaseUnit ?? 'percent') as 'percent' | 'currency',
      decreaseValue: wizardData.decreaseValue ?? 0,
      decreaseUnit: (wizardData.decreaseUnit ?? 'percent') as 'percent' | 'currency',
    }

    yield Effects.call(service, payload)

    // Booking na siatce korzysta z cache mapowania — bez czyszczenia zostają puste/stare komórki do odświeżenia strony
    clearAllPrecomputedPriceCaches()

    yield Effects.put({ type: PriceGridAction.CopyPricesSuccess })
    yield Effects.put({ type: PriceGridAction.RequestNewPrices, reload: true })
  } catch (e) {
    yield Effects.put({
      type: PriceGridAction.CopyPricesFailure,
      error: e instanceof Error ? e.message : String(e),
    })
  }
}

export default function*() {
  yield Effects.takeLatest(PriceGridAction.CopyPricesConfirm, copyPricelistSaga)
}
